import * as React from "react";
import * as L from "leaflet";
import { cssRule, forceRenderStyles } from "typestyle";
import { flushSync } from "react-dom";
import { createRoot, Root } from "react-dom/client";

cssRule(".nail-popup.leaflet-popup", {
  $nest: {
    "& .leaflet-popup-content-wrapper, & .leaflet-popup-tip": {
      fontFamily: "Inter Variable",
      background: "white",
      color: "black",
    },
    "& .leaflet-popup-content-wrapper": {
      borderRadius: "8px",
    },
    "& .leaflet-popup-content": {
      margin: "4px 18px",
    },
    "&.dense .leaflet-popup-content": {
      margin: 0,
    },
  },
});

cssRule(".react-popup.leaflet-popup", {
  $nest: {
    "& .leaflet-popup-content-wrapper, & .leaflet-popup-tip": {
      fontFamily: "Inter Variable",
      background: "white",
      color: "black",
    },
    "& .leaflet-popup-content-wrapper": {
      borderRadius: "12px",
    },
    "& .leaflet-popup-content": {
      margin: "0",
    },
  },
});

export function attachPopup(
  marker: L.Marker,
  content: string,
  mobilePopupOpenCallback?: () => void,
  isAlwaysOpen?: boolean,
): L.Marker {
  const popup = createPopup(content, isAlwaysOpen);

  marker.bindPopup(popup);
  if (!L.Browser.mobile && !isAlwaysOpen) {
    marker.on("mouseover", () => {
      marker.openPopup();
    });
    marker.on("mouseout", () => {
      marker.closePopup();
    });
  }
  if (L.Browser.mobile && mobilePopupOpenCallback) {
    marker.on("popupopen", mobilePopupOpenCallback);
  }
  return marker;
}

export function attachReactPopup(
  marker: L.Marker,
  reactElementConstructor: (
    updatePopupLayout: () => void,
    closePopup: () => void,
  ) => React.JSX.Element,
  onPopupOpen: () => void = () => null,
  onPopupClose: () => void = () => null,
): L.Marker {
  const popup = L.popup({ className: "react-popup", closeButton: false });

  marker.bindPopup(popup);

  // Leaflet tears down and rebuilds the popup element on each open/close
  // cycle, so the React root is recreated per open and disposed on close.
  let root: Root | null = null;

  marker.on("popupopen", () => {
    const popupEl = popup.getElement();
    const contentEl = popupEl.querySelector(".leaflet-popup-content");
    if (!contentEl) {
      return;
    }
    forceRenderStyles();

    root?.unmount();
    root = createRoot(contentEl);
    // Leaflet measures the popup to position it as soon as this handler
    // returns, so the tree has to be in the DOM synchronously.
    flushSync(() => {
      root.render(
        reactElementConstructor(
          popup.update.bind(popup),
          marker.closePopup.bind(marker),
        ),
      );
    });
    onPopupOpen();
  });

  marker.on("popupclose", () => {
    root?.unmount();
    root = null;
    onPopupClose();
  });

  return marker;
}

export function createPopup(content: string, isAlwaysOpen?: boolean) {
  return L.popup({
    className: "nail-popup",
    closeButton: false,
    autoClose: !isAlwaysOpen,
    closeOnClick: !isAlwaysOpen,
    autoPan: !isAlwaysOpen,
    minWidth: 0,
  })
    .setContent(
      `<div style="display: flex; justify-content: center; font-size: 14px; color: #000; line-height: 16px">` +
        content +
        `</div>`,
    )
    .openPopup();
}

export function createReactPopup(reactElement: React.JSX.Element) {
  const container = document.createElement("div");
  // `setContent` below measures the container, so the tree has to be rendered
  // synchronously rather than on React's usual async schedule.
  flushSync(() => {
    createRoot(container).render(reactElement);
  });
  forceRenderStyles();

  return L.popup({
    className: "nail-popup dense",
    closeButton: false,
  })
    .setContent(container)
    .openPopup();
}
